import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useUser } from './UserContext';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const { user } = useUser();

  useEffect(() => {
    const fetchCartItems = async () => {
      if (!user) {
        setCartItems([]);
        return;
      }

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        const { data } = await axios.get(`${API_BASE_URL}/api/users/cart`, config);
        setCartItems(data);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load cart.');
      }
    };

    fetchCartItems();
  }, [user]);

  const addToCart = async (product, quantity) => {
    if (!user) {
      toast.error('Please log in to add items to cart.');
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      };
      
      // Prepare request body with variation data if available
      const requestBody = {
        productId: product._id,
        quantity
      };
      
      if (product.selectedVariation) {
        requestBody.selectedVariation = {
          attributes: product.selectedVariation.selectedAttributes || product.selectedVariation.attributes,
          stock: product.selectedVariation.stock,
          price: product.selectedVariation.price,
          discountPrice: product.selectedVariation.discountPrice,
          // Include variation images - use first image if available
          images: product.selectedVariation.images && product.selectedVariation.images.length > 0
            ? product.selectedVariation.images
            : []
        };
      }
      
      const { data } = await axios.post(
        `${API_BASE_URL}/api/users/cart`,
        requestBody,
        config
      );
      setCartItems(data);
      
      // Build variation description for toast message
      let variationDesc = '';
      if (product.selectedVariation) {
        const attrs = product.selectedVariation.selectedAttributes || product.selectedVariation.attributes || {};
        variationDesc = ` (${Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(', ')})`;
      }
      
      toast.success(`${quantity} of ${product.name}${variationDesc} added to cart!`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add item to cart.');
    }
  };

  const removeFromCart = async (productId) => {
    if (!user) {
      toast.error('Please log in to remove items from cart.');
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      const { data } = await axios.delete(`${API_BASE_URL}/api/users/cart/${productId}`, config);
      setCartItems(data);
      toast.success('Item removed from cart.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove item from cart.');
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (!user) {
      toast.error('Please log in to update cart.');
      return;
    }

    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      };
      const { data } = await axios.put(
        `${API_BASE_URL}/api/users/cart`,
        { productId, quantity: newQuantity },
        config
      );
      setCartItems(data);
      toast.success('Cart quantity updated.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update quantity.');
    }
  };

  const clearCart = async () => {
    if (!user) {
      toast.error('Please log in to clear cart.');
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      await axios.delete(`${API_BASE_URL}/api/users/cart`, config);
      setCartItems([]);
      toast.success('Cart cleared successfully.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to clear cart.');
    }
  };

  return (
    <CartContext.Provider
      value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart }}
    >
      {children}
    </CartContext.Provider>
  );
};
